import {
  CHAT_MAX_ATTACHMENTS, isMediaType,
  type ChatAttachmentDto, type ChatMessageDto, type EditChange, type RevisionDto, type StudioToolMeta,
} from '@ads/shared';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import * as Ic from '../components/icons';
import { MARKS } from '../components/studioArt';
import { mediaUrl } from '../doc/Doc';
import { dateTime, rich } from '../lib/format';
import { useSession } from '../lib/queries';
import type { ArtifactChat, LiveTurn } from '../lib/useArtifactChat';

/* ─────────────────────────────────────────────────────────────────────
   A conversa com o agente, ao lado da peça. Pede-se em linguagem
   natural; fotos e arquivos entram por clique, arrastando ou colando.
   Cada resposta mostra o que mudou e a versão que criou; dá para
   desfazer a última ou voltar a qualquer versão.
   ───────────────────────────────────────────────────────────────────── */

const ACCEPT = '.png,.jpg,.jpeg,.webp,.gif,.pdf,.docx,.pptx,.xlsx,.txt,.md,.csv,.zip';
const READABLE = /\.(png|jpe?g|webp|gif|pdf|docx|pptx|xlsx|txt|md|csv|zip)$/i;
const MAX_PHOTO = 12 * 1024 * 1024;
const MAX_FILE = 50 * 1024 * 1024;

const SUGGEST: Record<'page' | 'document', string[]> = {
  page: ['Deixe os textos mais curtos e diretos', 'Inclua uma faixa de perguntas frequentes', 'Mude o link do botão para https://wa.me/55…', 'Título "…"'],
  document: ['Deixe o texto mais curto', 'Troque "…" por "…"', 'Título "…"', 'Use este arquivo como base para uma seção nova'],
};

const unitOf = (meta: StudioToolMeta, n: number) =>
  meta.key === 'site' ? `${n} ${n === 1 ? 'página' : 'páginas'}` : meta.output === 'page' ? `${n} ${n === 1 ? 'faixa' : 'faixas'}` : `${n} ${n === 1 ? 'seção' : 'seções'}`;

export function ChatPanel({ meta, chat, units, focusKey = 0 }: {
  meta: StudioToolMeta; chat: ArtifactChat; units: number;
  /** muda → a caixa de escrever ganha o foco (o "Editar com IA" da Biblioteca) */
  focusKey?: number;
}) {
  const { me } = useSession();
  const readOnly = me.org.role === 'viewer';
  const conv = chat.conversation;
  const art = chat.artifact;
  const [text, setText] = useState('');
  const [files, setFiles] = useState<Array<{ key: string; file: File; url: string | null }>>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [versions, setVersions] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const log = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const filesRef = useRef(files); filesRef.current = files;
  const [hello, setHello] = useState(false);
  const ready = art?.status === 'ready';
  const busy = chat.busy;
  const messages = conv?.messages ?? [];

  /* a última resposta que criou uma versão (é a que "Desfazer" desfaz) */
  const lastChange = useMemo(() => [...messages].reverse().find(m => m.role === 'agent' && m.revision && m.changes.length), [messages]);
  const head = conv?.head ?? art?.revision ?? 1;

  /* acompanha a conversa, a menos que a pessoa tenha subido para reler */
  useLayoutEffect(() => {
    const el = log.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, chat.live?.text, chat.live?.changes.length, chat.live?.phase, chat.error]);

  useEffect(() => () => { for (const f of filesRef.current) if (f.url) URL.revokeObjectURL(f.url); }, []);

  /* chegou pelo "Editar com IA": a conversa aparece e a caixa de escrever acende */
  useEffect(() => {
    const el = input.current;
    if (!focusKey || !ready || readOnly || !el) return;
    el.closest('.chat')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    el.focus({ preventScroll: true });
    setHello(true);
    const t = setTimeout(() => setHello(false), 1600);
    return () => clearTimeout(t);
  }, [focusKey, ready, readOnly]);

  const add = (list: FileList | File[]) => {
    setProblem(null);
    const next = [...files];
    for (const f of Array.from(list)) {
      if (next.length >= CHAT_MAX_ATTACHMENTS) { setProblem(`Até ${CHAT_MAX_ATTACHMENTS} anexos por mensagem.`); break; }
      if (!READABLE.test(f.name)) { setProblem(`${f.name}: envie fotos (JPG, PNG, WebP), PDF, Word, PowerPoint, Excel ou texto.`); continue; }
      const photo = /^image\//.test(f.type) || /\.(png|jpe?g|webp|gif)$/i.test(f.name);
      if (photo && f.size > MAX_PHOTO) { setProblem(`${f.name} passa de 12 MB. Exporte em JPEG com até 2.400 px no lado maior.`); continue; }
      if (f.size > MAX_FILE) { setProblem(`${f.name} passa de 50 MB.`); continue; }
      next.push({ key: `${f.name}-${f.size}-${f.lastModified}-${next.length}`, file: f, url: photo ? URL.createObjectURL(f) : null });
    }
    setFiles(next);
    input.current?.focus();
  };
  const remove = (key: string) => setFiles(fs => fs.filter(f => { if (f.key === key && f.url) URL.revokeObjectURL(f.url); return f.key !== key; }));

  const submit = async () => {
    if (busy || readOnly || !ready || (!text.trim() && !files.length)) return;
    const sending = files;
    const said = text.trim();
    stick.current = true;
    setText(''); setFiles([]); setProblem(null);
    const ok = await chat.send(said, sending.map(f => f.file));
    if (!ok) { setText(said); setFiles(sending); return; }
    for (const f of sending) if (f.url) URL.revokeObjectURL(f.url);
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void submit(); }
  };
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = Array.from(e.clipboardData.files);
    if (!pasted.length) return;
    e.preventDefault();
    /* imagem colada vem sem nome: dá um, com a extensão certa */
    add(pasted.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], `colada-${Date.now()}-${i}.${(f.type.split('/')[1] ?? 'png').replace('jpeg', 'jpg')}`, { type: f.type }))));
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault(); setDragging(false);
    if (!readOnly && ready && e.dataTransfer.files.length) add(e.dataTransfer.files);
  };
  const suggest = (s: string) => {
    if (/foto/i.test(s)) { setText(meta.output === 'page' ? 'Use esta foto na abertura' : 'Use esta foto para ilustrar'); picker.current?.click(); return; }
    setText(s);
    requestAnimationFrame(() => {
      const el = input.current;
      if (!el) return;
      el.focus();
      /* "…" é para preencher: o cursor já vai para lá */
      const at = s.indexOf('…');
      el.setSelectionRange(at >= 0 ? at : s.length, at >= 0 ? at + 1 : s.length);
    });
  };

  return (
    <aside className={`chat${dragging ? ' dragging' : ''}`} aria-label="Conversa com o agente"
      onDragOver={e => { if (!readOnly && ready && e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragging(true); } }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false); }}
      onDrop={onDrop}>
      <header className="chat-h">
        <span className="chat-av"><svg className="mk" viewBox="0 0 64 64" dangerouslySetInnerHTML={{ __html: MARKS[meta.key] }} /></span>
        <div className="chat-id">
          <b>Agente · {meta.name}</b>
          <span>{busy ? (chat.live?.phase || 'Trabalhando') + '…' : `Versão ${head}${conv ? ` · ${conv.editCost} tokens por alteração` : ''}`}</span>
        </div>
        {!!conv?.revisions.length && (
          <div className="chat-ver-wrap">
            <button className="chat-ver" type="button" aria-haspopup="menu" aria-expanded={versions} onClick={() => setVersions(v => !v)} title="Versões da peça">
              <Ic.History /><span>v{head}</span>
            </button>
            {versions && <Versions list={conv.revisions} head={head} busy={busy} onClose={() => setVersions(false)}
              onRestore={id => { setVersions(false); void chat.restore(id); }} />}
          </div>
        )}
      </header>

      <div className="chat-log" ref={log} aria-live="polite"
        onScroll={e => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }}>
        <div className="cm agent intro">
          <div className="bubble">
            {ready ? (
              <>
                <p>Pronto: {meta.deliverable.toLowerCase()} com {unitOf(meta, units)}. Agora é conversar — peça ajustes como pediria a alguém da equipe, e eu edito {meta.output === 'page' ? 'o site' : 'o documento'} aqui ao lado.</p>
                <p>Envie <b style={{ fontWeight: 560 }}>fotos</b> para eu pôr na peça{meta.output === 'page' ? ' (a melhor vai para a abertura; várias viram galeria)' : ''}, ou <b style={{ fontWeight: 560 }}>arquivos</b> para eu usar como base. Cada versão fica guardada.</p>
              </>
            ) : art?.status === 'failed' ? <p>Esta geração não terminou. Gere a peça de novo para conversar sobre ela.</p>
              : <p>Estou escrevendo a peça. A conversa abre assim que ela ficar pronta.</p>}
          </div>
          {ready && !messages.length && !busy && !readOnly && (
            <div className="chat-sugs">
              {[...(meta.output === 'page' ? ['Use uma foto na abertura'] : ['Envie uma foto para ilustrar']), ...SUGGEST[meta.output]].map(s => (
                <button key={s} type="button" onClick={() => suggest(s)}>{/foto/i.test(s) && <Ic.Image />}{s}</button>
              ))}
            </div>
          )}
        </div>

        {messages.map(m => (
          <Message key={m.id} m={m}
            undo={m.id === lastChange?.id && m.revision?.n === head && !busy && !readOnly && head > 1
              ? () => { const prev = conv?.revisions.find(r => r.n === m.revision!.n - 1); if (prev) void chat.restore(prev.id); }
              : undefined} />
        ))}
        {chat.live && <Live live={chat.live} />}
        {chat.error && !chat.live && (
          <div className="chat-err" role="alert"><Ic.Warning /><p>{chat.error}</p>
            <button type="button" aria-label="Fechar" onClick={chat.clearError}><Ic.Close /></button></div>
        )}
      </div>

      {readOnly ? (
        <p className="chat-ro"><Ic.Eye />Seu acesso a esta organização é de leitura: dá para acompanhar a conversa, não pedir alterações.</p>
      ) : (
        <form className={`chat-in${hello ? ' hello' : ''}`} onSubmit={e => { e.preventDefault(); void submit(); }}>
          {problem && <p className="chat-prob">{problem}</p>}
          {!!files.length && (
            <ul className="chat-files">
              {files.map(f => (
                <li key={f.key} className={f.url ? 'img' : 'doc'} title={f.file.name}>
                  {f.url ? <img src={f.url} alt="" /> : <><Ic.Doc /><span>{f.file.name}</span></>}
                  <button type="button" aria-label={`Tirar ${f.file.name}`} onClick={() => remove(f.key)}><Ic.Close /></button>
                </li>
              ))}
            </ul>
          )}
          <textarea ref={input} value={text} rows={2} maxLength={4000} disabled={!ready}
            placeholder={ready ? (files.length ? 'Diga o que fazer com os anexos (ou envie assim)' : 'Peça um ajuste, ou arraste fotos e arquivos para cá') : 'A conversa abre quando a peça ficar pronta'}
            onChange={e => setText(e.target.value)} onKeyDown={onKey} onPaste={onPaste}
            onInput={e => { const el = e.currentTarget; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 180)}px`; }} />
          <div className="chat-tools">
            <button className="chat-clip" type="button" disabled={!ready || busy} onClick={() => picker.current?.click()} title="Anexar fotos ou arquivos">
              <Ic.Clip /><span className="sr">Anexar</span>
            </button>
            <input ref={picker} type="file" multiple accept={ACCEPT} hidden onChange={e => { if (e.target.files) add(e.target.files); e.target.value = ''; }} />
            <span className="chat-hint">⏎ envia · ⇧⏎ quebra a linha</span>
            {busy && chat.live?.reply ? (
              <button className="chat-send stop" type="button" onClick={() => void chat.cancel()} title="Parar"><Ic.Stop /><span className="sr">Parar</span></button>
            ) : (
              <button className="chat-send" type="submit" disabled={busy || !ready || (!text.trim() && !files.length)} title="Enviar">
                {busy ? <span className="spin" /> : <Ic.ArrowUp />}<span className="sr">Enviar</span>
              </button>
            )}
          </div>
        </form>
      )}
      {dragging && <div className="chat-drop" aria-hidden="true"><Ic.Upload /><b>Solte para anexar</b><span>Fotos vão para a peça; arquivos, para o agente ler.</span></div>}
    </aside>
  );
}

/* ── uma mensagem ─────────────────────────────────────────────────── */

const paragraphs = (t: string) => t.split(/\n{2,}/).map(s => s.trim()).filter(Boolean);

function Message({ m, undo }: { m: ChatMessageDto; undo?: () => void }) {
  if (m.role === 'user') {
    return (
      <div className="cm user">
        {!!m.attachments.length && <Attachments list={m.attachments} />}
        {m.text && <div className="bubble">{paragraphs(m.text).map((p, i) => <p key={i}>{p}</p>)}</div>}
        <span className="cm-at">{m.createdBy ? `${m.createdBy} · ` : ''}{dateTime(m.createdAt)}</span>
      </div>
    );
  }
  /* volta a uma versão: uma linha no meio da conversa */
  if (m.revision && !m.changes.length && m.status === 'done') {
    return <div className="cm-sys"><Ic.Undo /><span>{m.text}</span><em>v{m.revision.n}</em></div>;
  }
  return (
    <div className={`cm agent${m.status === 'failed' ? ' failed' : ''}`}>
      <div className="bubble">
        {paragraphs(m.text).map((p, i) => <p key={i}>{rich(p)}</p>)}
        {m.status === 'failed' && <p className="cm-fail"><Ic.Warning />{m.error ?? 'A rodada falhou. Nada foi mudado.'}</p>}
        {!!m.changes.length && <Changes list={m.changes} />}
      </div>
      <span className="cm-at">
        {m.revision ? <em className="cm-v">v{m.revision.n}</em> : null}
        {m.status === 'done' ? (m.cost ? `${m.cost} tokens` : 'sem custo') : null}
        <span>· {dateTime(m.createdAt)}</span>
        {undo && <button type="button" className="cm-undo" onClick={undo}><Ic.Undo />Desfazer</button>}
      </span>
    </div>
  );
}

const KIND: Record<EditChange['kind'], { label: string; cls: string }> = {
  replace: { label: 'Reescrito', cls: 'rep' }, insert: { label: 'Novo', cls: 'ins' }, delete: { label: 'Removido', cls: 'del' }, title: { label: 'Título', cls: 'tit' },
};

function Changes({ list }: { list: EditChange[] }) {
  const [open, setOpen] = useState(list.length <= 4);
  const shown = open ? list : list.slice(0, 3);
  return (
    <ul className="chg">
      {shown.map((c, i) => <li key={i} data-k={KIND[c.kind].cls}><i>{KIND[c.kind].label}</i><span>{c.label}</span></li>)}
      {!open && <li className="more"><button type="button" onClick={() => setOpen(true)}>e mais {list.length - 3}</button></li>}
    </ul>
  );
}

function Attachments({ list, local }: { list: ChatAttachmentDto[]; local?: Array<{ key: string; name: string; preview: string | null }> }) {
  const items = local ?? list.map(a => ({ key: a.id, name: a.filename, preview: a.image || isMediaType(a.contentType) ? mediaUrl(a.id) : null }));
  return (
    <ul className={`cm-files n${Math.min(items.length, 3)}`}>
      {items.map(a => (
        <li key={a.key} className={a.preview ? 'img' : 'doc'} title={a.name}>
          {a.preview ? <img src={a.preview} alt={a.name} loading="lazy" /> : <><Ic.Doc /><span>{a.name}</span></>}
        </li>
      ))}
    </ul>
  );
}

/* ── a rodada em curso ────────────────────────────────────────────── */

function Live({ live }: { live: LiveTurn }) {
  const req = live.request;
  return (
    <>
      {req ? (
        <div className="cm user">
          {!!req.attachments.length && <Attachments list={req.attachments} />}
          {req.text && <div className="bubble">{paragraphs(req.text).map((p, i) => <p key={i}>{p}</p>)}</div>}
        </div>
      ) : !!live.uploading.length && (
        <div className="cm user sending">
          <Attachments list={[]} local={live.uploading.map(u => ({ key: u.key, name: u.file.name, preview: u.preview }))} />
        </div>
      )}
      <div className="cm agent running">
        <div className="bubble">
          {live.text ? paragraphs(live.text).map((p, i) => <p key={i}>{rich(p)}</p>) : null}
          {!!live.changes.length && (
            <ul className="chg">{live.changes.map((c, i) => <li key={i} data-k={KIND[c.kind].cls}><i>{KIND[c.kind].label}</i><span>{c.label}</span></li>)}</ul>
          )}
          <p className="cm-phase"><span className="cm-dots"><i /><i /><i /></span>{live.phase || 'Pensando'}</p>
        </div>
      </div>
    </>
  );
}

/* ── as versões ───────────────────────────────────────────────────── */

const SOURCE: Record<RevisionDto['source'], string> = { generation: 'Gerada pelo agente', chat: 'Conversa', restore: 'Volta a uma versão' };

function Versions({ list, head, busy, onRestore, onClose }: { list: RevisionDto[]; head: number; busy: boolean; onRestore: (id: string) => void; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const out = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) onClose(); };
    const esc = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', out); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc); };
  }, [onClose]);
  return (
    <div className="chat-vers" role="menu" ref={box}>
      <div className="menu-h"><b>Versões da peça</b><span>Voltar a uma versão cria uma nova — nada se perde.</span></div>
      <ol>
        {list.map(r => (
          <li key={r.id} data-head={r.n === head || undefined}>
            <span className="v-n">v{r.n}</span>
            <span className="v-tx">
              <b>{r.note ? `“${r.note.length > 64 ? r.note.slice(0, 63) + '…' : r.note}”` : SOURCE[r.source]}</b>
              <em>{SOURCE[r.source]} · {dateTime(r.createdAt)}{r.createdBy ? ` · ${r.createdBy}` : ''}</em>
            </span>
            {r.n === head ? <span className="v-now">Atual</span>
              : <button type="button" role="menuitem" disabled={busy} onClick={() => onRestore(r.id)}>Voltar</button>}
          </li>
        ))}
      </ol>
    </div>
  );
}

